import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {
    Dialog,
    Title,
    Flex,
    Text,
    Icon,
    PriorityIcon,
    Button,
    Separator,
    CheckBox
} from '@priority-software/priority-style-react';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import EntityIcon from '../../../components/Common/EntityIcon/EntityIcon';

import styles from '../styles/PersonalizationDialog.module.scss';
import {ItemsList} from '../../../components/Common/ItemsList';
import {personalize, setNeverShowAgain, startNewCounting} from '../actions';
import {showEntityHelpMessage} from '../../../_shared/Help/actions/helpActions';

import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {sendMonitor} from '../../../utils/monitoring';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {PersonalizationType, extractDataByPersonalizationType} from '../utils/personalization';
import {
    addSoonzedSuggestion,
    resetPersonalizationSuggestion
} from '../../../services/indexDB/indexeddbPersonalizationCenter';
import {SuggestionType} from '../types/personalizationCenter';
import {configuration} from '../config';

interface ItemProps {
    title: string;
    name: string;
    exec: string;
    isChecked: boolean;
}

interface PersonalizationProps {
    type: PersonalizationType;
    items: ItemProps[];
    db: any;
    configuration: any;
    texts: any;
    showEntityIcon: boolean;
    isRefresh?: boolean;
    isDoneEnabled?: boolean;
    isSnoozed?: boolean;
}

export const PersonalizationDialog = observer(
    forwardRef((props, ref) => {
        const [type, setType] = useState(PersonalizationType.None);
        const [conf, setConf] = useState(configuration);
        const [db, setDb] = useState({});
        const [texts, setTexts] = useState({} as any);
        const [showEntityIcon, setShowEntityIcon] = useState(false);

        const [items, setItems] = useState([]);
        const [selectedItems, setSelectedItems] = useState([]);
        const [isOpen, setIsOpen] = useState(false);
        const [success, setSuccess] = useState(false);
        const [isDoneEnabled, setIsDoneEnabled] = useState(false);
        const [isSnoozed, setIsSnoozed] = useState(false);

        useEffect(() => {
            isOpen && handleClose();
        }, [formsStore?.currentForm]);

        useImperativeHandle(ref, () => {
            return {
                handleClose,
                showDialog
            };
        });

        const showDialog = (props: PersonalizationProps) => {
            if (isOpen && !props?.isRefresh) return;
            if (
                formsStore?.currentForm?.maydesignform ||
                props.type === PersonalizationType.Homepage
            ) {
                const {
                    items,
                    type,
                    db,
                    configuration,
                    texts,
                    showEntityIcon,
                    isDoneEnabled,
                    isSnoozed
                } = props;

                setDb(db);
                setTexts(texts);
                setConf(configuration);
                setType(type);
                setShowEntityIcon(showEntityIcon);
                setIsDoneEnabled(isDoneEnabled);
                setIsSnoozed(isSnoozed);

                setItems(items);
                setIsOpen(true);
                returnFocus();
            }
        };

        const handleClose = () => {
            if (isOpen) {
                setIsOpen(false);
                setSuccess(false);
                setSelectedItems([]);
                returnFocus();
            }
        };
        const resetCenterSuggestion = () => {
            if (isSnoozed) {
                switch (type) {
                    case PersonalizationType.Actions:
                        resetPersonalizationSuggestion(SuggestionType.Actions);
                        break;
                    case PersonalizationType.Homepage:
                        resetPersonalizationSuggestion(SuggestionType.Homepage);
                        break;
                    case PersonalizationType.SubForms:
                        resetPersonalizationSuggestion(SuggestionType.Subforms);
                        break;
                }
            }
        };

        const handlePersonalization = async () => {
            if (selectedItems.length > 0) {
                setSuccess(true);
                await personalize(type, formsStore.currentFormName, selectedItems, isSnoozed);
            }
        };

        const handleNeverShowAgain = () => {
            setNeverShowAgain(conf.neverAllKey, db, type);
            resetCenterSuggestion();
            handleClose();
        };

        const handleNotNow = () => {
            if (!isSnoozed) {
                startNewCounting(db, formsStore.currentFormName, type);
                const data = extractDataByPersonalizationType(type, formsStore.currentForm?.name);
                data && addSoonzedSuggestion(data);
            }
            handleClose();
        };

        const handleDone = () => {
            if (!isSnoozed) {
                startNewCounting(db, formsStore.currentFormName, type, true);
                sendMonitor('Recommendation_HP_Done');
            } else {
                resetPersonalizationSuggestion(SuggestionType.Homepage);
            }
            handleClose();
        };

        const handleToggleCheckitem = item => {
            const updated = [...selectedItems];
            const indexToRemove = selectedItems.findIndex(itm => itm.name === item.name);

            indexToRemove > -1 ? updated.splice(indexToRemove, 1) : updated.push(item);
            setSelectedItems(updated);
        };

        const handleSelectAll = () => {
            items.length === selectedItems.length ? setSelectedItems([]) : setSelectedItems(items);
        };

        const PersonalizationItem = ({item}) => {
            return (
                <Flex componentClasses={styles.subFormItem} justifyContent="flex-start">
                    <CheckBox
                        onChange={() => handleToggleCheckitem(item.item)}
                        checked={selectedItems.some(itm => itm.name === item.item.name)}
                    />
                    {showEntityIcon && (
                        <span className={styles.entityIcon}>
                            <EntityIcon type={item.item.type} />
                        </span>
                    )}
                    <Text skin="paragraph-1" textColor="primary-700">
                        {item.item.title}
                    </Text>
                    <Icon
                        onClick={e => showEntityHelpMessage(item.item.exec, e?.target)}
                        icon={PriorityIcon.help}
                        iconColor="primary-700"
                    />
                </Flex>
            );
        };

        const PersonalizationHomepageItem = ({item}) => {
            return (
                <Flex componentClasses={styles.subFormItem} justifyContent="flex-start">
                    {
                        <span className={styles.entityIcon}>
                            <EntityIcon type={item.item.type} />
                        </span>
                    }
                    <Text
                        skin="paragraph-1"
                        textColor="primary-700"
                        componentClasses={styles.marginHorizontalGrow}
                    >
                        {item.item.title}
                    </Text>
                    <Button
                        componentClasses={styles.blueButton}
                        onClick={() => {
                            personalize(PersonalizationType.Homepage, item.item, items, isSnoozed);
                        }}
                        skin="text"
                    >
                        {texts.add}
                    </Button>
                </Flex>
            );
        };
        const MainTitle = (
            <div>
                <Flex justifyContent="flex-start">
                    <Icon
                        componentClasses={styles.marginHorizontal}
                        icon={PriorityIcon.bulb}
                        iconColor="primary-700"
                    />
                    <Title skin="h5" textColor="primary-700" text={texts.upperTitle} />
                </Flex>
                {!success && (
                    <>
                        <Text
                            componentClasses={classNames(
                                'dialog-drag-handle',
                                styles.marginVertical
                            )}
                            skin="paragraph-1"
                            textColor="primary-700"
                        >
                            {texts.itemsSubTitle}
                        </Text>
                    </>
                )}
            </div>
        );

        const Content = (
            <>
                <ItemsList
                    autoHeight
                    items={items}
                    renderItem={item => {
                        if (type === PersonalizationType.Homepage) {
                            return <PersonalizationHomepageItem item={item} />;
                        }

                        return <PersonalizationItem item={item} />;
                    }}
                />

                <div className={styles.footer}>
                    {type !== PersonalizationType.Homepage && (
                        <Flex justifyContent="space-between">
                            <CheckBox
                                componentClasses={styles.selectAllBtn}
                                checked={items.length === selectedItems.length}
                                onChange={handleSelectAll}
                            >
                                <Text skin="subtitle-1" textColor="primary-500">
                                    {texts.selectAll}
                                </Text>
                            </CheckBox>

                            <Flex justifyContent="flex-end">
                                <Button
                                    size="small"
                                    componentClasses={styles.marginHorizontal}
                                    onClick={handlePersonalization}
                                    disabled={selectedItems.length === 0}
                                >
                                    {texts.hide}
                                </Button>
                            </Flex>
                        </Flex>
                    )}
                    <Separator
                        componentClasses={classNames(styles.marginVertical, styles.separatorColor)}
                        align="vertical"
                        height={1}
                    />
                    <Flex justifyContent="space-between">
                        {type !== PersonalizationType.Homepage && (
                            <Flex justifyContent="space-between">
                                <Button onClick={handleNeverShowAgain} skin="text">
                                    {texts.neverShowAll}
                                </Button>
                                <Button onClick={handleNotNow} skin="text">
                                    {texts.snooze}
                                </Button>
                            </Flex>
                        )}
                        {type === PersonalizationType.Homepage && (
                            <Flex justifyContent="space-between">
                                <Flex justifyContent="flex-start">
                                    <Button onClick={handleNotNow} skin="text">
                                        {texts.snooze}
                                    </Button>
                                    <Separator
                                        componentClasses={styles.separator}
                                        align="horizontal"
                                        height={25}
                                    />
                                    <Button onClick={handleNeverShowAgain} skin="text">
                                        {texts.dontShowAgain}
                                    </Button>
                                </Flex>
                                <Button
                                    disabled={!isDoneEnabled}
                                    onClick={handleDone}
                                    skin="primary"
                                >
                                    {texts.done}
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                </div>
            </>
        );

        const SuccessMessage = (
            <div>
                <Title textColor="primary-700" skin="h4">
                    {texts.successTitle}
                </Title>
                <Text componentClasses={styles.footer} skin="paragraph-1">
                    {texts.successContent}
                </Text>
                <Button componentClasses={styles.footer} onClick={handleClose} skin="primary">
                    {texts.successOk}
                </Button>
            </div>
        );

        return (
            isOpen && (
                <Dialog
                    isOpen={isOpen}
                    content={success ? SuccessMessage : Content}
                    title={MainTitle}
                    componentClasses={styles.dialog}
                    onClose={handleClose}
                    mask={false}
                    draggable
                    closable={false}
                />
            )
        );
    })
);
